import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { formatRelative } from '@/utils/format';
import type { InstitutionUser, Campus, RoleDefinition } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { TableSkeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { KebabMenu } from '@/components/ui/Menu';
import { Icon } from '@/components/icons';
import styles from './AdminUsersPage.module.css';

const STATUS_CLASS: Record<string, string> = {
  ACTIVE: 'statusActive',
  INVITED: 'statusInvited',
  SUSPENDED: 'statusSuspended',
  EXPIRED: 'statusExpired',
};

const STATUSES = ['ACTIVE', 'INVITED', 'SUSPENDED', 'EXPIRED'] as const;

/** An invitation older than this without acceptance deserves attention. */
const STALE_INVITE_DAYS = 7;

/** Rows per page — enough to scan, few enough to stay orientable. */
const PAGE_SIZE = 25;

type SortKey = 'name' | 'email' | 'roleName' | 'status' | 'lastLoginAt';

interface EditForm {
  name: string;
  title: string;
  phone: string;
  referenceNumber: string;
  department: string;
  campusId: string;
  roleCode: string;
  roleStartDate: string;
  roleEndDate: string;
}

const editFormFor = (user: InstitutionUser): EditForm => ({
  name: user.name,
  title: user.title ?? '',
  phone: user.phone ?? '',
  referenceNumber: user.referenceNumber ?? '',
  department: user.department ?? '',
  campusId: user.campusId ?? '',
  roleCode: user.roleCode,
  roleStartDate: user.roleStartDate?.slice(0, 10) ?? '',
  roleEndDate: user.roleEndDate?.slice(0, 10) ?? '',
});

export function AdminUsersPage() {
  const { t, i18n } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload, replace } = useAsyncData<InstitutionUser[]>(
    `admin-users-${institutionId}`,
    () => api.admin.users.list(institutionId),
    !!institutionId,
  );

  const { state: rolesState } = useAsyncData<RoleDefinition[]>(
    // Scoped to the institution: its TYPE decides which roles exist here, so a
    // secondary school's invite dialog never offers Professor or Dean.
    `admin-role-defs-${institutionId}`,
    () => api.admin.roles.listDefinitions(institutionId),
    !!institutionId,
  );

  const { state: campusesState } = useAsyncData<Campus[]>(
    `admin-campuses-${institutionId}`,
    () => api.admin.campuses.list(institutionId),
    !!institutionId,
  );

  const toast = useToast();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  // Mail-not-configured fallback: identity hands back the exact invite link so
  // the administrator can deliver it by hand instead of hitting a dead end.
  const [shareLink, setShareLink] = useState<{ email: string; url: string } | null>(null);
  const EMPTY_INVITE = {
    roleCode: '',
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    campusId: '',
    referenceNumber: '',
    department: '',
  };
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [inviting, setInviting] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // The person being inspected, edited or removed. Details is the hub: a row
  // click opens it, and edit/remove launch from there or from the table.
  const [detailUser, setDetailUser] = useState<InstitutionUser | null>(null);
  const [editUser, setEditUser] = useState<InstitutionUser | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<InstitutionUser | null>(null);

  // Bulk selection: ids of checked rows. Kept as a set; cleared when the
  // filter changes would silently act on rows the admin can no longer see.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Filters live in the URL so a refresh, a shared link, or the back button
  // lands on the same filtered view instead of silently resetting it.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('q') ?? '';
  const statusFilter = searchParams.get('status') ?? '';
  const roleFilter = searchParams.get('role') ?? '';
  const campusFilter = searchParams.get('campus') ?? '';
  const updateFilters = useCallback(
    (patch: { q?: string; status?: string; role?: string; campus?: string }) => {
      setSelected(new Set());
      setSearchParams(
        (params) => {
          for (const [key, value] of Object.entries(patch)) {
            if (value) params.set(key, value);
            else params.delete(key);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [emailError, setEmailError] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const [page, setPage] = useState(1);
  const searchRef = useRef<HTMLInputElement>(null);

  // "/" focuses search from anywhere on the page — the fastest path to a
  // person is typing their name, so the shortcut keeps hands on the keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const emailLooksValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

  const users = state.status === 'ready' ? state.data : [];
  const roles = rolesState.status === 'ready' ? rolesState.data : [];
  const campuses = campusesState.status === 'ready' ? campusesState.data : [];

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { ACTIVE: 0, INVITED: 0, SUSPENDED: 0, EXPIRED: 0 };
    for (const user of users) counts[user.status] = (counts[user.status] ?? 0) + 1;
    return counts;
  }, [users]);

  // ACTIVE accounts whose holder has never signed in — access granted but
  // unused, which is either onboarding friction or an account to reclaim.
  const isDormant = (user: InstitutionUser) => user.status === 'ACTIVE' && !user.lastLoginAt;
  const dormantFilter = statusFilter === 'DORMANT';
  const dormantCount = useMemo(() => users.filter(isDormant).length, [users]);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = users.filter((user) => {
      if (dormantFilter) {
        if (!isDormant(user)) return false;
      } else if (statusFilter && user.status !== statusFilter) return false;
      if (roleFilter && user.roleCode !== roleFilter) return false;
      if (campusFilter && user.campusId !== campusFilter) return false;
      if (!query) return true;
      return (
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.roleName.toLowerCase().includes(query) ||
        (user.referenceNumber ?? '').toLowerCase().includes(query) ||
        (user.department ?? '').toLowerCase().includes(query)
      );
    });
    const { key, dir } = sort;
    return [...filtered].sort((a, b) => {
      const left = (a[key] ?? '') as string;
      const right = (b[key] ?? '') as string;
      return left.localeCompare(right) * dir;
    });
  }, [users, search, statusFilter, dormantFilter, roleFilter, campusFilter, sort]);

  // Pagination keeps long registers orientable; filters reset to page one so
  // a narrowed result is never hidden behind a stale page number.
  const pageCount = Math.max(1, Math.ceil(visibleUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedUsers = visibleUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, roleFilter, campusFilter]);

  const hasFilters = !!(search || statusFilter || roleFilter || campusFilter);
  const clearFilters = () => updateFilters({ q: '', status: '', role: '', campus: '' });

  const toggleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  const sortIndicator = (key: SortKey) =>
    sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : '';

  const staleInvite = (user: InstitutionUser): boolean => {
    if (user.status !== 'INVITED' || !user.invitedAt) return false;
    return Date.now() - new Date(user.invitedAt).getTime() > STALE_INVITE_DAYS * 86_400_000;
  };

  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(t('admin.users.emailCopied'));
    } catch {
      toast.error(t('common.actionFailed'));
    }
  };

  // ── Invite (unchanged flow) ────────────────────────────────────────────

  // What the reference and department fields MEAN depends on who is being
  // invited: a staff ID and department for staff, a matriculation number and
  // programme for a student, the linked student's reference for a parent.
  const inviteKind =
    inviteForm.roleCode === 'STUDENT'
      ? 'student'
      : ['PARENT', 'GUARDIAN'].includes(inviteForm.roleCode)
        ? 'parent'
        : inviteForm.roleCode
          ? 'staff'
          : '';

  // Error prevention beats error recovery: an email already in the register
  // is caught while typing, named with its status, and the submit is blocked —
  // the fix (resend, or reactivate) is a different button, not a duplicate row.
  const duplicateOf = useMemo(() => {
    const email = inviteForm.email.trim().toLowerCase();
    if (!email) return null;
    return users.find((u) => u.email.toLowerCase() === email) ?? null;
  }, [users, inviteForm.email]);

  const inviteReady =
    emailLooksValid(inviteForm.email) &&
    !duplicateOf &&
    !!inviteForm.roleCode &&
    !!inviteForm.firstName.trim() &&
    !!inviteForm.lastName.trim();

  const handleInvite = async () => {
    if (!institutionId) return;
    setInviting(true);
    try {
      const body: { email: string; roleCode: string } & Record<string, string> = {
        email: inviteForm.email,
        roleCode: inviteForm.roleCode,
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
      };
      for (const key of ['title', 'phone', 'campusId', 'referenceNumber', 'department'] as const) {
        if (inviteForm[key].trim()) body[key] = inviteForm[key].trim();
      }
      const result = await api.admin.users.invite(institutionId, body);
      setInviteModalOpen(false);
      setInviteForm(EMPTY_INVITE);
      // Report what actually happened, not what was hoped for.
      const invitation = result.invitation;
      if (invitation && !invitation.issued) {
        toast.error(t('admin.users.inviteIssueFailed', { email: body.email }));
      } else if (invitation && !invitation.delivered && invitation.inviteUrl) {
        setShareLink({ email: body.email, url: invitation.inviteUrl });
        toast.info(t('admin.users.inviteNotDelivered'));
      } else {
        toast.success(t('admin.users.inviteSuccessToast', { email: body.email }));
      }
      reload();
    } catch {
      // The modal stays open with the form intact for a retry.
      toast.error(t('common.actionFailed'));
    } finally {
      setInviting(false);
    }
  };

  // ── Row actions ────────────────────────────────────────────────────────

  const handleAction = useCallback(async (userId: string, action: 'resend' | 'suspend' | 'reactivate') => {
    if (!institutionId) return;
    setActionLoading(userId);
    try {
      if (action === 'resend') {
        // The server reissues the actual identity invitation and says what
        // happened; the toast repeats that truth rather than assuming success.
        const result = await api.admin.users.resendInvitation(institutionId, userId);
        const invitation = result.invitation;
        if (invitation && !invitation.issued) {
          toast.error(t('admin.users.inviteIssueFailed', { email: result.email }));
        } else if (invitation && !invitation.delivered && invitation.inviteUrl) {
          setShareLink({ email: result.email, url: invitation.inviteUrl });
          toast.info(t('admin.users.inviteNotDelivered'));
        } else {
          toast.success(t('admin.users.invitationResent'));
        }
      } else if (action === 'suspend') {
        await api.admin.users.suspend(institutionId, userId);
        if (users) replace(users.map((u) => u.id === userId ? { ...u, status: 'SUSPENDED' as const } : u));
        // Forgiveness over confirmation: suspension acts immediately and the
        // toast carries the way back, instead of an "are you sure?" upfront.
        toast.success(t('admin.users.userSuspended'), {
          actionLabel: t('common.undo'),
          onAction: () => void handleAction(userId, 'reactivate'),
        });
      } else if (action === 'reactivate') {
        await api.admin.users.reactivate(institutionId, userId);
        if (users) replace(users.map((u) => u.id === userId ? { ...u, status: 'ACTIVE' as const } : u));
        toast.success(t('admin.users.userReactivated'));
      }
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setActionLoading(null);
    }
  }, [institutionId, users, replace, t, toast]);

  const openEdit = (user: InstitutionUser) => {
    setDetailUser(null);
    setEditUser(user);
    setEditForm(editFormFor(user));
  };

  const handleSaveEdit = async () => {
    if (!institutionId || !editUser || !editForm) return;
    setSaving(true);
    try {
      const updated = await api.admin.users.update(institutionId, editUser.id, {
        name: editForm.name.trim(),
        title: editForm.title,
        phone: editForm.phone,
        referenceNumber: editForm.referenceNumber,
        department: editForm.department,
        campusId: editForm.campusId,
        roleCode: editForm.roleCode,
        roleStartDate: editForm.roleStartDate,
        roleEndDate: editForm.roleEndDate,
      });
      replace(users.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
      setEditUser(null);
      setEditForm(null);
      toast.success(t('admin.users.userUpdated', { name: updated.name }));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!institutionId || !removing) return;
    setSaving(true);
    try {
      await api.admin.users.remove(institutionId, removing.id);
      replace(users.filter((u) => u.id !== removing.id));
      setRemoving(null);
      setDetailUser(null);
      toast.success(t('admin.users.userRemoved'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ── Bulk actions ───────────────────────────────────────────────────────

  const selectedUsers = visibleUsers.filter((u) => selected.has(u.id));
  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisibleSelected =
    pagedUsers.length > 0 && pagedUsers.every((u) => selected.has(u.id));
  const toggleAllVisible = () =>
    setSelected(
      allVisibleSelected
        ? new Set([...selected].filter((id) => !pagedUsers.some((u) => u.id === id)))
        : new Set([...selected, ...pagedUsers.map((u) => u.id)]),
    );

  /** One draft to everyone selected — BCC so recipients don't see each other. */
  const emailSelected = () => {
    const emails = selectedUsers.map((u) => u.email).join(',');
    window.location.href = `mailto:?bcc=${encodeURIComponent(emails)}`;
  };

  /**
   * Runs one action across the selection, one call at a time, and reports the
   * honest tally. Only rows the action can apply to are attempted — bulk
   * "suspend" quietly skipping invited users would otherwise read as failure.
   */
  const runBulk = async (
    action: 'resend' | 'suspend' | 'reactivate',
    applicable: (u: InstitutionUser) => boolean,
  ) => {
    if (!institutionId) return;
    const targets = selectedUsers.filter(applicable);
    if (targets.length === 0) return;
    setBulkBusy(true);
    let done = 0;
    let failed = 0;
    const updated = new Map<string, InstitutionUser['status']>();
    for (const target of targets) {
      try {
        if (action === 'resend') await api.admin.users.resendInvitation(institutionId, target.id);
        if (action === 'suspend') {
          await api.admin.users.suspend(institutionId, target.id);
          updated.set(target.id, 'SUSPENDED');
        }
        if (action === 'reactivate') {
          await api.admin.users.reactivate(institutionId, target.id);
          updated.set(target.id, 'ACTIVE');
        }
        done += 1;
      } catch {
        failed += 1;
      }
    }
    if (updated.size > 0) {
      replace(users.map((u) => (updated.has(u.id) ? { ...u, status: updated.get(u.id)! } : u)));
    }
    setSelected(new Set());
    setBulkBusy(false);
    if (failed === 0) toast.success(t('admin.users.bulkDone', { count: done }));
    else toast.error(t('admin.users.bulkPartial', { done, failed }));
  };

  // ── CSV export ─────────────────────────────────────────────────────────

  /** Exports what is on screen — the filtered, sorted view, or the selection. */
  const exportCsv = () => {
    const rows = selectedUsers.length > 0 ? selectedUsers : visibleUsers;
    const cell = (value: unknown) => {
      const text = String(value ?? '');
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const header = ['email', 'name', 'title', 'phone', 'role', 'campus', 'status', 'reference', 'department', 'invitedAt', 'lastLoginAt'];
    const lines = [
      header.join(','),
      ...rows.map((u) =>
        [u.email, u.name, u.title, u.phone, u.roleName, u.campusName, u.status, u.referenceNumber, u.department, u.invitedAt, u.lastLoginAt]
          .map(cell)
          .join(','),
      ),
    ];
    // BOM so Excel opens accented names as UTF-8 instead of mojibake.
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('admin.users.exported', { count: rows.length }));
  };

  const formatLogin = (login?: string) => {
    if (!login) return '—';
    try {
      return formatRelative(login, i18n.language);
    } catch {
      return login;
    }
  };

  const campusName = (id?: string | null) =>
    campuses.find((c) => c.id === id)?.name ?? '—';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t('admin.users.title')}</h1>
          <p className={styles.headerSubtitle}>{t('admin.users.subtitle')}</p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" onClick={exportCsv} disabled={visibleUsers.length === 0}>
            {t('admin.users.exportCsv')}
          </Button>
          <Button onClick={() => setInviteModalOpen(true)}>{t('admin.users.inviteUser')}</Button>
        </div>
      </header>

      {state.status === 'loading' && (
        <Card aria-busy="true">
          <TableSkeleton rows={6} columns={6} />
        </Card>
      )}

      {state.status === 'error' && (
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.users.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.error')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      )}

      {state.status === 'ready' && users.length === 0 && (
        <Card className={styles.emptyState}>
          <Icon name="users" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.users.noUsers')}</h2>
          <p className={styles.emptyStateBody}>{t('admin.campuses.createFirst')}</p>
          <Button onClick={() => setInviteModalOpen(true)}>{t('admin.users.inviteUser')}</Button>
        </Card>
      )}

      {state.status === 'ready' && users.length > 0 && (
        <Card>
          {/* Where the population stands, and one click to filter to a slice. */}
          <div className={styles.statChips} role="group" aria-label={t('admin.users.status')}>
            <button
              type="button"
              className={`${styles.statChip} ${statusFilter === '' ? styles.statChipActive : ''}`}
              onClick={() => updateFilters({ status: '' })}
            >
              {t('admin.users.filterAllStatuses')}
              <span className={styles.statChipCount}>{users.length}</span>
            </button>
            {STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                className={`${styles.statChip} ${statusFilter === status ? styles.statChipActive : ''}`}
                onClick={() => updateFilters({ status: statusFilter === status ? '' : status })}
              >
                {t(`admin.status.${status.toLowerCase()}`)}
                <span className={styles.statChipCount}>{statusCounts[status] ?? 0}</span>
              </button>
            ))}
            {/* Access granted, never used — worth a chip of its own because it
                is invisible inside the ACTIVE count. */}
            {dormantCount > 0 && (
              <button
                type="button"
                className={`${styles.statChip} ${styles.statChipWarn} ${dormantFilter ? styles.statChipActive : ''}`}
                onClick={() => updateFilters({ status: dormantFilter ? '' : 'DORMANT' })}
              >
                {t('admin.users.dormant')}
                <span className={styles.statChipCount}>{dormantCount}</span>
              </button>
            )}
          </div>

          <div className={styles.toolbar}>
            <input
              ref={searchRef}
              type="search"
              className={styles.searchInput}
              placeholder={t('admin.users.searchShortcutPlaceholder')}
              aria-label={t('admin.users.searchPlaceholder')}
              value={search}
              onChange={(e) => updateFilters({ q: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && search) {
                  e.stopPropagation();
                  updateFilters({ q: '' });
                }
              }}
            />
            <Select
              aria-label={t('admin.users.role')}
              className={styles.statusFilter}
              searchable
              value={roleFilter}
              onChange={(value) => updateFilters({ role: value })}
              options={[
                { value: '', label: t('admin.users.filterAllRoles') },
                ...roles.map((r) => ({ value: r.code, label: r.name })),
              ]}
            />
            <Select
              aria-label={t('admin.users.campus')}
              className={styles.statusFilter}
              searchable
              value={campusFilter}
              onChange={(value) => updateFilters({ campus: value })}
              options={[
                { value: '', label: t('admin.users.filterAllCampuses') },
                ...campuses.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            <span className={styles.resultCount} aria-live="polite">
              {t('admin.users.resultCount', { shown: visibleUsers.length, total: users.length })}
            </span>
          </div>

          {/* Appears only while something is selected; the actions offered say
              how many rows they will actually touch. */}
          {selected.size > 0 && (
            <div className={styles.bulkBar}>
              <span className={styles.bulkCount}>
                {t('admin.users.selectedCount', { count: selected.size })}
              </span>
              {selectedUsers.some((u) => u.status === 'INVITED') && (
                <Button size="sm" variant="secondary" loading={bulkBusy} disabled={bulkBusy}
                  onClick={() => runBulk('resend', (u) => u.status === 'INVITED')}>
                  {t('admin.users.bulkResend', { count: selectedUsers.filter((u) => u.status === 'INVITED').length })}
                </Button>
              )}
              {selectedUsers.some((u) => u.status === 'ACTIVE') && (
                <Button size="sm" variant="secondary" loading={bulkBusy} disabled={bulkBusy}
                  onClick={() => runBulk('suspend', (u) => u.status === 'ACTIVE')}>
                  {t('admin.users.bulkSuspend', { count: selectedUsers.filter((u) => u.status === 'ACTIVE').length })}
                </Button>
              )}
              {selectedUsers.some((u) => u.status === 'SUSPENDED') && (
                <Button size="sm" variant="secondary" loading={bulkBusy} disabled={bulkBusy}
                  onClick={() => runBulk('reactivate', (u) => u.status === 'SUSPENDED')}>
                  {t('admin.users.bulkReactivate', { count: selectedUsers.filter((u) => u.status === 'SUSPENDED').length })}
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={emailSelected} disabled={bulkBusy}>
                {t('admin.users.emailSelected')}
              </Button>
              <Button size="sm" variant="secondary" onClick={exportCsv} disabled={bulkBusy}>
                {t('admin.users.exportSelected')}
              </Button>
              <button type="button" className={styles.clearSelection} onClick={() => setSelected(new Set())}>
                {t('admin.users.clearSelection')}
              </button>
            </div>
          )}

          <div className="table-scroll">
            <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkboxCell}>
                  <input
                    type="checkbox"
                    aria-label={t('admin.users.selectAll')}
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th aria-sort={sort.key === 'name' ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
                  <button type="button" className={styles.sortHeader} onClick={() => toggleSort('name')}>
                    {t('admin.users.name')}{sortIndicator('name')}
                  </button>
                </th>
                <th aria-sort={sort.key === 'email' ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
                  <button type="button" className={styles.sortHeader} onClick={() => toggleSort('email')}>
                    {t('admin.users.email')}{sortIndicator('email')}
                  </button>
                </th>
                <th aria-sort={sort.key === 'roleName' ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
                  <button type="button" className={styles.sortHeader} onClick={() => toggleSort('roleName')}>
                    {t('admin.users.role')}{sortIndicator('roleName')}
                  </button>
                </th>
                <th>{t('admin.users.campus')}</th>
                <th aria-sort={sort.key === 'status' ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
                  <button type="button" className={styles.sortHeader} onClick={() => toggleSort('status')}>
                    {t('admin.users.status')}{sortIndicator('status')}
                  </button>
                </th>
                <th aria-sort={sort.key === 'lastLoginAt' ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
                  <button type="button" className={styles.sortHeader} onClick={() => toggleSort('lastLoginAt')}>
                    {t('admin.users.lastLogin')}{sortIndicator('lastLoginAt')}
                  </button>
                </th>
                <th>{t('admin.users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={8} className={styles.noMatchesCell}>
                    {t('admin.users.noMatches')}
                    {hasFilters && (
                      <>
                        {' '}
                        <button type="button" className={styles.clearSelection} onClick={clearFilters}>
                          {t('admin.users.clearFilters')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              )}
              {pagedUsers.map((user) => (
                <tr
                  key={user.id}
                  className={styles.rowClickable}
                  tabIndex={0}
                  aria-label={t('admin.users.openDetails', { name: user.name })}
                  onClick={() => setDetailUser(user)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                      e.preventDefault();
                      setDetailUser(user);
                    }
                  }}
                >
                  <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={t('admin.users.selectUserRow', { name: user.name })}
                      checked={selected.has(user.id)}
                      onChange={() => toggleSelected(user.id)}
                    />
                  </td>
                  <td>
                    <div className={styles.nameCell}>{user.name}</div>
                    {(user.department || user.referenceNumber) && (
                      <div className={styles.userSub}>
                        {[user.department, user.referenceNumber].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <span className={styles.emailCell}>
                      {user.email}
                      <button
                        type="button"
                        className={styles.iconBtn}
                        aria-label={t('admin.users.copyEmail')}
                        title={t('admin.users.copyEmail')}
                        onClick={() => copyEmail(user.email)}
                      >
                        <Icon name="checklist" size={14} />
                      </button>
                    </span>
                  </td>
                  <td>{user.roleName}</td>
                  <td>{user.campusName ?? campusName(user.campusId)}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[user.status] ?? 'statusExpired']}`}>
                      {t(`admin.status.${user.status.toLowerCase()}`)}
                    </span>
                    {staleInvite(user) && user.invitedAt && (
                      <div className={styles.staleBadge}>
                        {t('admin.users.invitedAgo', { when: formatRelative(user.invitedAt, i18n.language) })}
                      </div>
                    )}
                  </td>
                  <td>
                    {formatLogin(user.lastLoginAt)}
                    {isDormant(user) && (
                      <div className={styles.staleBadge}>{t('admin.users.neverSignedIn')}</div>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* One visible action — the most likely one for the row's
                        state — and everything else behind the three dots. */}
                    <div className={styles.actions}>
                      {user.status === 'INVITED' && (
                        <Button size="sm" variant="secondary" onClick={() => handleAction(user.id, 'resend')} loading={actionLoading === user.id} disabled={actionLoading === user.id}>{t('admin.users.resendInvitation')}</Button>
                      )}
                      {user.status === 'ACTIVE' && (
                        <Button size="sm" variant="secondary" onClick={() => handleAction(user.id, 'suspend')} loading={actionLoading === user.id} disabled={actionLoading === user.id}>{t('admin.users.suspend')}</Button>
                      )}
                      {user.status === 'SUSPENDED' && (
                        <Button size="sm" variant="secondary" onClick={() => handleAction(user.id, 'reactivate')} loading={actionLoading === user.id} disabled={actionLoading === user.id}>{t('admin.users.reactivate')}</Button>
                      )}
                      <KebabMenu
                        ariaLabel={t('admin.users.moreActions', { name: user.name })}
                        items={[
                          { label: t('admin.users.viewDetails'), onClick: () => setDetailUser(user) },
                          { label: t('common.edit'), onClick: () => openEdit(user) },
                          { label: t('admin.users.copyEmail'), onClick: () => void copyEmail(user.email) },
                          {
                            label: t('admin.users.sendEmail'),
                            onClick: () => { window.location.href = `mailto:${user.email}`; },
                          },
                          {
                            label: t('admin.users.remove'),
                            danger: true,
                            disabled: user.status === 'ACTIVE',
                            title: user.status === 'ACTIVE' ? t('admin.users.removeBlockedActive') : undefined,
                            onClick: () => setRemoving(user),
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className={styles.pager}>
              <span className={styles.pagerInfo}>
                {t('admin.users.pageRange', {
                  from: (currentPage - 1) * PAGE_SIZE + 1,
                  to: Math.min(currentPage * PAGE_SIZE, visibleUsers.length),
                  total: visibleUsers.length,
                })}
              </span>
              <div className={styles.pagerButtons}>
                <Button size="sm" variant="secondary" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                  {t('common.previous')}
                </Button>
                <span className={styles.pagerPage}>
                  {currentPage} / {pageCount}
                </span>
                <Button size="sm" variant="secondary" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ── User details: the whole record, and every action in one place ── */}
      <Modal
        open={!!detailUser}
        onClose={() => setDetailUser(null)}
        title={detailUser?.name ?? ''}
        description={detailUser ? `${detailUser.roleName} · ${detailUser.email}` : undefined}
      >
        {detailUser && (
          <div className={styles.dialogForm}>
            <dl className={styles.detailGrid}>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.status')}</dt>
                <dd>
                  <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[detailUser.status] ?? 'statusExpired']}`}>
                    {t(`admin.status.${detailUser.status.toLowerCase()}`)}
                  </span>
                </dd>
              </div>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.phone')}</dt>
                <dd>{detailUser.phone || '—'}</dd>
              </div>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.campus')}</dt>
                <dd>{detailUser.campusName ?? campusName(detailUser.campusId)}</dd>
              </div>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.referenceLabel')}</dt>
                <dd>{detailUser.referenceNumber || '—'}</dd>
              </div>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.department')}</dt>
                <dd>{detailUser.department || '—'}</dd>
              </div>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.invitedAt')}</dt>
                <dd>{detailUser.invitedAt ? formatRelative(detailUser.invitedAt, i18n.language) : '—'}</dd>
              </div>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.lastLogin')}</dt>
                <dd>{formatLogin(detailUser.lastLoginAt)}</dd>
              </div>
              <div className={styles.detailRow}>
                <dt>{t('admin.users.mfa')}</dt>
                <dd>{detailUser.mfaEnabled ? t('admin.users.mfaEnabled') : '—'}</dd>
              </div>
            </dl>

            <div className={styles.dialogActions}>
              <Button variant="secondary" onClick={() => copyEmail(detailUser.email)}>
                {t('admin.users.copyEmail')}
              </Button>
              <Button variant="secondary" onClick={() => { window.location.href = `mailto:${detailUser.email}`; }}>
                {t('admin.users.sendEmail')}
              </Button>
              <Button onClick={() => openEdit(detailUser)}>{t('common.edit')}</Button>
              <Button
                variant="danger"
                disabled={detailUser.status === 'ACTIVE'}
                title={detailUser.status === 'ACTIVE' ? t('admin.users.removeBlockedActive') : undefined}
                onClick={() => setRemoving(detailUser)}
              >
                {t('admin.users.remove')}
              </Button>
            </div>
            {detailUser.status === 'ACTIVE' && (
              <p className={styles.detailHint}>{t('admin.users.removeBlockedActive')}</p>
            )}
          </div>
        )}
      </Modal>

      {/* ── Edit: everything correctable — except the email ── */}
      <Modal
        open={!!editUser}
        onClose={() => { setEditUser(null); setEditForm(null); }}
        title={t('admin.users.editDialogTitle')}
        description={editUser?.email}
        closeOnBackdropClick={false}
      >
        {editUser && editForm && (
          <div className={styles.dialogForm} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); if (editForm.name.trim() && !saving) handleSaveEdit(); } }}>
            <FormField id="edit-email" label={t('admin.users.email')} hint={t('admin.users.emailLocked')}>
              <input id="edit-email" className={styles.formField} value={editUser.email} disabled />
            </FormField>

            <div className={styles.fieldRow}>
              <FormField id="edit-title" label={t('admin.users.titleLabel')}>
                <Select
                  id="edit-title"
                  value={editForm.title}
                  onChange={(value) => setEditForm({ ...editForm, title: value })}
                  placeholder={t('admin.users.titleNone')}
                  options={[
                    { value: '', label: t('admin.users.titleNone') },
                    { value: 'Prof.', label: t('admin.users.titleProf') },
                    { value: 'Dr.', label: t('admin.users.titleDr') },
                    { value: 'Mr.', label: t('admin.users.titleMr') },
                    { value: 'Mrs.', label: t('admin.users.titleMrs') },
                    { value: 'Ms.', label: t('admin.users.titleMs') },
                  ]}
                />
              </FormField>
              <FormField id="edit-name" label={t('admin.users.name')} required>
                <input
                  id="edit-name"
                  className={styles.formField}
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </FormField>
            </div>

            <FormField id="edit-phone" label={t('admin.users.phone')}>
              <input
                id="edit-phone"
                type="tel"
                className={styles.formField}
                placeholder={t('admin.users.phonePlaceholder')}
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </FormField>

            <FormField id="edit-role" label={t('admin.users.role')}>
              <Select
                id="edit-role"
                searchable
                value={editForm.roleCode}
                onChange={(value) => setEditForm({ ...editForm, roleCode: value })}
                options={roles.map((r) => ({ value: r.code, label: r.name, description: r.description }))}
              />
            </FormField>

            <FormField id="edit-campus" label={t('admin.users.campus')}>
              <Select
                id="edit-campus"
                searchable
                value={editForm.campusId}
                onChange={(value) => setEditForm({ ...editForm, campusId: value })}
                placeholder={t('admin.users.selectCampus')}
                options={[
                  { value: '', label: t('admin.users.noCampus') },
                  ...campuses
                    .filter((c) => c.status === 'ACTIVE' || c.status === 'DRAFT')
                    .map((c) => ({
                      value: c.id,
                      label: c.name,
                      ...(c.status === 'DRAFT' ? { description: t('admin.status.draft') } : {}),
                    })),
                ]}
              />
            </FormField>

            <div className={styles.fieldRow}>
              <FormField id="edit-reference" label={t('admin.users.referenceLabel')}>
                <input
                  id="edit-reference"
                  className={styles.formField}
                  value={editForm.referenceNumber}
                  onChange={(e) => setEditForm({ ...editForm, referenceNumber: e.target.value })}
                />
              </FormField>
              <FormField id="edit-department" label={t('admin.users.department')}>
                <input
                  id="edit-department"
                  className={styles.formField}
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                />
              </FormField>
            </div>

            <div className={styles.fieldRow}>
              <FormField id="edit-role-start" label={t('admin.roles.startDate')} hint={t('admin.users.roleValidityHint')}>
                <input
                  id="edit-role-start"
                  type="date"
                  className={styles.formField}
                  value={editForm.roleStartDate}
                  onChange={(e) => setEditForm({ ...editForm, roleStartDate: e.target.value })}
                />
              </FormField>
              <FormField id="edit-role-end" label={t('admin.roles.endDate')}>
                <input
                  id="edit-role-end"
                  type="date"
                  className={styles.formField}
                  min={editForm.roleStartDate || undefined}
                  value={editForm.roleEndDate}
                  onChange={(e) => setEditForm({ ...editForm, roleEndDate: e.target.value })}
                />
              </FormField>
            </div>

            <div className={styles.dialogActions}>
              <Button variant="secondary" onClick={() => { setEditUser(null); setEditForm(null); }}>{t('common.cancel')}</Button>
              <Button onClick={handleSaveEdit} loading={saving} disabled={!editForm.name.trim() || saving}>{t('common.save')}</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Remove: confirmed, and impossible while the user is ACTIVE ── */}
      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={t('admin.users.removeConfirmTitle')}
        description={removing ? `${removing.name} · ${removing.email}` : undefined}
      >
        <div className={styles.dialogForm}>
          <p className={styles.detailHint}>{t('admin.users.removeConfirmBody')}</p>
          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={() => setRemoving(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" onClick={handleRemove} loading={saving} disabled={saving}>
              {t('admin.users.remove')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        title={t('admin.users.inviteDialogTitle')}
        description={t('admin.users.inviteDialogBody')}
        closeOnBackdropClick={false}
      >
        <div className={styles.dialogForm} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); if (inviteReady && !inviting) handleInvite(); } }}>
          {/* Role first: it decides which of the fields below make sense. */}
          <FormField id="invite-role" label={t('admin.users.selectRole')} required>
            <Select
              id="invite-role"
              searchable
              value={inviteForm.roleCode}
              onChange={(value) => setInviteForm({ ...inviteForm, roleCode: value })}
              placeholder={t('admin.users.selectRole')}
              options={roles.map((r) => ({ value: r.code, label: r.name, description: r.description }))}
            />
          </FormField>

          <div className={styles.fieldRow}>
            <FormField id="invite-title" label={t('admin.users.titleLabel')}>
              <Select
                id="invite-title"
                value={inviteForm.title}
                onChange={(value) => setInviteForm({ ...inviteForm, title: value })}
                placeholder={t('admin.users.titleNone')}
                options={[
                  { value: '', label: t('admin.users.titleNone') },
                  { value: 'Prof.', label: t('admin.users.titleProf') },
                  { value: 'Dr.', label: t('admin.users.titleDr') },
                  { value: 'Mr.', label: t('admin.users.titleMr') },
                  { value: 'Mrs.', label: t('admin.users.titleMrs') },
                  { value: 'Ms.', label: t('admin.users.titleMs') },
                ]}
              />
            </FormField>
            <FormField id="invite-first-name" label={t('admin.users.firstName')} required>
              <input
                id="invite-first-name"
                className={styles.formField}
                placeholder={t('admin.users.firstNamePlaceholder')}
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
              />
            </FormField>
            <FormField id="invite-last-name" label={t('admin.users.lastName')} required>
              <input
                id="invite-last-name"
                className={styles.formField}
                placeholder={t('admin.users.lastNamePlaceholder')}
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
              />
            </FormField>
          </div>

          <FormField
            id="invite-email"
            label={t('admin.users.email')}
            required
            error={
              emailError ||
              (duplicateOf
                ? t('admin.users.duplicateEmail', {
                    name: duplicateOf.name,
                    status: t(`admin.status.${duplicateOf.status.toLowerCase()}`),
                  })
                : undefined)
            }
            hint={duplicateOf ? t('admin.users.duplicateEmailHint') : undefined}
          >
            <input
              id="invite-email"
              type="email"
              className={styles.formField}
              placeholder={t('admin.users.emailPlaceholder')}
              value={inviteForm.email}
              aria-invalid={!!emailError || !!duplicateOf}
              onChange={(e) => {
                setInviteForm({ ...inviteForm, email: e.target.value });
                if (emailError && emailLooksValid(e.target.value)) setEmailError('');
              }}
              onBlur={() => {
                setEmailError(
                  inviteForm.email && !emailLooksValid(inviteForm.email)
                    ? t('common.invalidEmail')
                    : '',
                );
              }}
            />
          </FormField>

          <FormField id="invite-phone" label={t('admin.users.phone')}>
            <input
              id="invite-phone"
              type="tel"
              className={styles.formField}
              placeholder={t('admin.users.phonePlaceholder')}
              value={inviteForm.phone}
              onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
            />
          </FormField>

          <FormField id="invite-campus" label={t('admin.users.selectCampus')}>
            <Select
              id="invite-campus"
              searchable
              value={inviteForm.campusId}
              onChange={(value) => setInviteForm({ ...inviteForm, campusId: value })}
              placeholder={t('admin.users.selectCampus')}
              // DRAFT included: a campus is born DRAFT, and filtering to ACTIVE
              // made every freshly created campus invisible here — the "no
              // options" mystery. Only suspended/closed campuses are excluded.
              options={campuses
                .filter((c) => c.status === 'ACTIVE' || c.status === 'DRAFT')
                .map((c) => ({
                  value: c.id,
                  label: c.name,
                  ...(c.status === 'DRAFT' ? { description: t('admin.status.draft') } : {}),
                }))}
            />
          </FormField>

          {/* Academic identity, phrased for the role: a lecturer has a staff
              ID and a department; a student has a matriculation number and a
              programme; a parent is tied to a student's reference. */}
          {inviteKind && (
            <FormField
              id="invite-reference"
              label={
                inviteKind === 'student'
                  ? t('admin.users.refStudent')
                  : inviteKind === 'parent'
                    ? t('admin.users.refParent')
                    : t('admin.users.refStaff')
              }
              hint={inviteKind === 'parent' ? t('admin.users.refParentHint') : undefined}
            >
              <input
                id="invite-reference"
                className={styles.formField}
                placeholder={
                  inviteKind === 'student'
                    ? t('admin.users.refStudentPlaceholder')
                    : inviteKind === 'parent'
                      ? t('admin.users.refParentPlaceholder')
                      : t('admin.users.refStaffPlaceholder')
                }
                value={inviteForm.referenceNumber}
                onChange={(e) => setInviteForm({ ...inviteForm, referenceNumber: e.target.value })}
              />
            </FormField>
          )}
          {(inviteKind === 'staff' || inviteKind === 'student') && (
            <FormField
              id="invite-department"
              label={
                inviteKind === 'student'
                  ? t('admin.users.programme')
                  : t('admin.users.department')
              }
            >
              <input
                id="invite-department"
                className={styles.formField}
                placeholder={
                  inviteKind === 'student'
                    ? t('admin.users.programmePlaceholder')
                    : t('admin.users.departmentPlaceholder')
                }
                value={inviteForm.department}
                onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
              />
            </FormField>
          )}

          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={() => setInviteModalOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleInvite} loading={inviting} disabled={!inviteReady || inviting}>{t('admin.users.sendInvitation')}</Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!shareLink}
        onClose={() => setShareLink(null)}
        title={t('admin.users.inviteLinkTitle')}
        description={shareLink ? t('admin.users.inviteLinkBody', { email: shareLink.email }) : undefined}
        closeOnBackdropClick={false}
      >
        {shareLink && (
          <div className={styles.dialogForm}>
            <input
              className={styles.formField}
              type="text"
              readOnly
              value={shareLink.url}
              onFocus={(e) => e.target.select()}
              aria-label={t('admin.users.inviteLinkTitle')}
            />
            <div className={styles.dialogActions}>
              <Button variant="secondary" onClick={() => setShareLink(null)}>{t('common.close')}</Button>
              <Button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareLink.url);
                    toast.success(t('common.linkCopied'));
                  } catch {
                    // Clipboard can be denied; the field above stays selectable.
                    toast.error(t('common.actionFailed'));
                  }
                }}
              >
                {t('common.copyLink')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
